import { SingleValue } from "react-select";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../components/layouts/card/card";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { FEATURES } from "../../../../../models/Plan";
import {
    NOTIFY_REFUND_WHEN,
    NOTIFY_REFUND_WHEN_ARR,
    VALIDATION_RESTOCK_TRIGGER,
    VALIDATION_RESTOCK_TRIGGER_ARR
} from "../../../../../models/User";
import currentPortalStore from "../../../../../stores/current-portal-store";
import {observer} from "mobx-react";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    dataName?: string; /** used by Candu to target an element */
}

const ItemsValidationCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const storeSetting = currentPortalStore.setting;
    const isValidationFeatureAllowed = storeSetting && currentPortalStore.isFeatureAllowed(FEATURES.VALIDATE_ITEMS)
    const hasNotifyRefundIntegration = currentPortalStore.currentPortal.hasNotifyRefundIntegration;

    const onChangeValidationRestockTrigger = (value: SingleValue<IOption>) => {
        storeSetting?.setValidationRestockTrigger(value?.value as VALIDATION_RESTOCK_TRIGGER);
    }

    const selectedValidationRestockTrigger = (): IOption => {
        return VALIDATION_RESTOCK_TRIGGER_ARR.filter((i) => i.value === storeSetting?.validationRestockTrigger).map((i) => ({
            label: getTranslation(i.label, pageScope),
            value: i.value
        }))[0];
    };

    const onChangeNotifyRefundWhen = (value: SingleValue<IOption>) => {
        storeSetting?.setNotifyRefundWhen(value?.value as NOTIFY_REFUND_WHEN);
    }

    const selectedNotifyRefundWhen = (): IOption => {
        return NOTIFY_REFUND_WHEN_ARR.filter((i) => i.value === storeSetting?.notifyRefundWhen).map((i) => ({
            label: getTranslation(i.label, pageScope),
            value: i.value
        }))[0];
    };

    return <Card
        toolbar={{
            title: (
                <FontBody1 variant={FONT_VARIANT.BOLD}>
                    <DashTranslation translationKey="ITEMS_VALIDATION" scope={pageScope} />
                </FontBody1>
            )
        }}
        expandCallback={onExpandClick}
        isExpanded={isExpanded}
        dataName={dataName}
    >
        <FormFieldLine
            label={getTranslation('DOCUMENT_SHIPMENT_INSPECTION', pageScope)}
            note={getTranslation('ADD_A_STEP_IN_RECEIVING_PROCESS_WHERE_YOU_DOCUMENT_ITEMS_RETURNED', pageScope)}
            tooltip={!isValidationFeatureAllowed 
                ? <DashTranslation translationKey="FEATURE_NOT_INCLUDED_IN_YOUR_PLAN" scope="dashboard-general" />
                : ''}
            dataName="document-shipment-inspection"
        >
            <Toggle
                disabled={!isValidationFeatureAllowed}
                checked={storeSetting?.isValidationEnabled}
                onChange={storeSetting?.setIsValidationEnabled}
                label=''
            />
        </FormFieldLine>
        {
            storeSetting?.isValidationEnabled && <><FormFieldLine
                label={getTranslation('INSPECTION_DOCUMENTATION_IS_MANDATORY', pageScope)}
                note={getTranslation('SHIPMENT_DOCUMENTATION_WILL_BE_REQUIRED', pageScope)}
                dataName="inspection-documentation-is-mandatory"
            >
                <Toggle
                    checked={storeSetting.isRestockNoteMandatory}
                    onChange={storeSetting.setIsRestockNoteMandatory}
                    label=''
                />
            </FormFieldLine>
            <FormFieldLine
                label={getTranslation('VALIDATE_ALL_ITEMS_TITLE', pageScope)}
                note={getTranslation('VALIDATE_ALL_ITEMS_NOTE', pageScope)}
                dataName="validate-all-items"
            >
                <Toggle
                    checked={storeSetting.isValidateAllEnabled}
                    onChange={storeSetting.setIsValidateAllEnabled}
                    label=''
                />
            </FormFieldLine>
            </>
        }
        {(currentPortalStore?.isFeatureLoaded([FEATURES.BETA_VALIDATION_RESTOCK_TRIGGER_AUTOMATION]) 
            && currentPortalStore?.isFeatureAllowed(FEATURES.BETA_VALIDATION_RESTOCK_TRIGGER_AUTOMATION)) && (
            <FormFieldLine
                label={getTranslation('RESTOCK_ITEMS_IN_RESTOCKABLE_CONDITION', pageScope)}
                note={getTranslation('RESTOCK_ITEMS_IN_RESTOCKABLE_CONDITION_NOTE', pageScope)}
                dataName="restock-items-in-restockable-condition"
            >
                <SelectDropdown
                    options={VALIDATION_RESTOCK_TRIGGER_ARR.map((i) => ({
                        label: getTranslation(i.label, pageScope),
                        value: i.value
                    }))}
                    onChange={onChangeValidationRestockTrigger}
                    value={selectedValidationRestockTrigger()}
                />
            </FormFieldLine>
        )}

        { hasNotifyRefundIntegration && (
            <FormFieldLine
                label={getTranslation('NOTIFY_REFUND_WHEN', pageScope)}
                dataName="notify-refund-when"
            >
                <SelectDropdown
                    options={NOTIFY_REFUND_WHEN_ARR.map((i) => ({
                        label: getTranslation(i.label, pageScope),
                        value: i.value
                    }))}
                    onChange={onChangeNotifyRefundWhen}
                    value={selectedNotifyRefundWhen()}
                />
            </FormFieldLine>
        )}
        
    </Card>
})

export default ItemsValidationCard;
